import { gql } from '@apollo/client';

import { COMPANY_FRAGMENT } from './fragments';

export const UserProfileUpdateMutation = gql`
  mutation User_updateProfile($input: UpdateUserProfileInput!) {
    User_updateProfile(input: $input) {
      id
      firstname
      lastname
      emailOnlySignature
      signatures {
        language
        signature
        body
      }
    }
  }
`;

export const WelcomeInfoUpdateMutation = gql`
  mutation Store_onboarding(
    $storeOnboarding: StoreOnboardingInput!
    $user: UpdateUserProfileInput!
    $brand: UpdateBrandInput!
  ) {
    Store_onboarding(input: $storeOnboarding) {
      company {
        ...CompanyFragment
      }
      promoCode
      id
    }
    User_updateProfile(input: $user) {
      firstname
      lastname
      id
    }
    Brand_updateDefault(input: $brand) {
      brandUrl
      id
    }
  }
  ${COMPANY_FRAGMENT}
`;

export const WelcomeStatisticsUpdateMutation = gql`
  mutation Store_update($input: StoreUpdateInput!) {
    Store_update(input: $input) {
      statistics {
        shipmentsPerMonth
        whatCanWeDo
        clientSource
        partnerName
      }
      hasPendingOnboarding
      id
      onboardingSteps {
        step
        completed
      }
    }
  }
`;

export const CompanyInfoUpdateMutation = gql`
  mutation Store_update($input: StoreUpdateInput!) {
    Store_update(input: $input) {
      company {
        ...CompanyFragment
      }
      id
    }
  }
  ${COMPANY_FRAGMENT}
`;

export const TrackingModeMutation = gql`
  mutation Store_update($input: StoreUpdateInput!) {
    Store_update(input: $input) {
      id
      onboardingSteps {
        step
        completed
      }
    }
  }
`;

export const SkipOnboardingStepMutation = gql`
  mutation Store_update($step: OnboardingSteps!) {
    Store_skipOnboardingStep(step: $step) {
      id
      onboardingSteps {
        step
        completed
      }
    }
  }
`;

export const FeatureRequestMutation = gql`
  mutation Store_featureRequest($feature: StoreFeature!) {
    Store_featureRequest(feature: $feature)
  }
`;

export const BrandUpdateSlugMutation = gql`
  mutation Brand_updateSlug($input: UpdateSlugInput!) {
    Brand_updateSlug(input: $input) {
      id
      slug
      trackingSlug
      logo
      brandUrl
      type
    }
  }
`;

export const BrandCreateCustomMutation = gql`
  mutation Brand_createCustom($input: CreateCustomBrandInput!) {
    Brand_createCustom(input: $input) {
      id
      slug
      trackingSlug
      logo
      brandUrl
      type
    }
  }
`;

export const CreateWarehouseMutation = gql`
  mutation Warehouse_createWarehouse($settings: WarehouseUpdateInput!) {
    Warehouse_createWarehouse(settings: $settings)
  }
`;

export const UpdateWarehouseMutation = gql`
  mutation Warehouse_updateConfig($settings: WarehouseUpdateInput!, $warehouseId: String!) {
    Warehouse_updateConfig(settings: $settings, warehouseId: $warehouseId)
  }
`;

export const DeleteWarehouseMutation = gql`
  mutation Warehouse_deleteWarehouse($warehouseId: ID!) {
    Warehouse_deleteWarehouse(warehouseId: $warehouseId)
  }
`;

export const MakeDefaultWarehouseMutation = gql`
  mutation Warehouse_makeDefault($warehouseId: ID!) {
    Warehouse_makeDefault(warehouseId: $warehouseId)
  }
`;

export const DuplicateWarehouseMutation = gql`
  mutation Warehouse_duplicate($warehouseId: ID!) {
    Warehouse_duplicate(warehouseId: $warehouseId)
  }
`;

export const CreatePackageMutation = gql`
  mutation Store_addPackaging($packaging: PackagingInput!) {
    Store_addPackaging(packaging: $packaging) {
      id
    }
  }
`;

export const UpdatePackageMutation = gql`
  mutation Store_changePackaging($packaging: PackagingInput!, $packId: String!) {
    Store_changePackaging(packaging: $packaging, packId: $packId) {
      id
    }
  }
`;

export const UpdatePrintingMutation = gql`
  mutation Store_printConfig($printing: StorePrintingSettings!) {
    Store_printConfig(printing: $printing) {
      documentPrintingType
      printerType
      optimizeBulkPrinting
      id
      priorityLabelType
      useDirectPrintingApp
    }
  }
`;

export const UpdateShippingRulesSortingIndexMutation = gql`
  mutation ShippingRule_changeOrdering($sortedRules: [String]!) {
    ShippingRule_changeOrdering(sortedRules: $sortedRules)
  }
`;

export const UpdateShippingRulesSortingIndexPartialMutation = gql`
  mutation ShippingRule_changeOrderingPartial($sortedRules: [ID]!) {
    ShippingRule_changeOrderingPartial(sortedRules: $sortedRules)
  }
`;

export const UpdateShippingRuleMutation = gql`
  mutation ShippingRule_updateConfig($ruleId: String!, $config: ShippingRuleInput) {
    ShippingRule_updateConfig(ruleId: $ruleId, config: $config)
  }
`;

export const UpdateFulfillmentSettingsMutation = gql`
  mutation Store_printingAndPickingConfig($config: StorePrintingAndPicking!) {
    Store_printingAndPickingConfig(config: $config) {
      id
      isScanAndPackEnabled
      useLargerFont
      outOfStockRule
      pickingWithBaskets {
        enabled
        maxBasketsPerPickingList
        maxProductsPerBasket
      }
      printProductionList
      printingReferenceType
      groupProductsInShippingQueue
      validation {
        cardId
      }
      skipPickingQueue
      scanAndPack {
        allowManualPicking
        allowScanPackaging
        groupProductsBy
        matchingScannedNumber
        printAutomatically
        processSingleMultiSeparately
        showOrderMessage
      }
    }
  }
`;

export const DuplicateShippingRuleMutation = gql`
  mutation ShippingRule_duplicateRule($rule: String!) {
    ShippingRule_duplicateRule(rule: $rule)
  }
`;

export const DeleteShippingRuleMutation = gql`
  mutation ShippingRule_deleteRule($rule: String!) {
    ShippingRule_deleteRule(rule: $rule)
  }
`;

export const CreateShippingRuleMutation = gql`
  mutation ShippingRule_createRule($config: ShippingRuleInput!) {
    ShippingRule_createRule(config: $config)
  }
`;

export const UpdateCourierCharacteristicsMutation = gql`
  mutation Courier_updateCharacteristics($courierId: ID!, $characteristics: JSON!) {
    Courier_updateCharacteristics(courierId: $courierId, characteristics: $characteristics)
  }
`;

export const UpdateCourierCustomizationMutation = gql`
  mutation Courier_customization($courierId: ID!, $alias: String, $email: String) {
    Courier_customization(courierId: $courierId, alias: $alias, email: $email)
  }
`;

export const UpdateCourierAllowed3PLs = gql`
  mutation Courier_customization($allowed3Pl: [ID], $courierId: ID!) {
    Courier_customization(allowed3PL: $allowed3Pl, courierId: $courierId)
  }
`;

export const ManageWarehousesMutation = gql`
  mutation Courier_manageWarehouses($courierId: ID!, $warehouses: [String]!) {
    Courier_manageWarehouses(courierId: $courierId, warehouses: $warehouses)
  }
`;

export const UpdateCourierSurchargesMutation = gql`
  mutation Courier_setSurcharges(
    $courier: ID!
    $rateCalculations: ApplySurchargeInput
    $cashOnDeliveries: ApplySurchargeInput
    $insurances: ApplySurchargeInput
    $remoteZones: ApplySurchargeInput
    $weightSurcharges: ApplySurchargeInput
    $dimensionSurcharges: ApplySurchargeInput
    $exportDuties: ApplySurchargeInput
    $fuelSurcharges: ApplySurchargeInput
    $parcelSurcharges: ApplySurchargeInput
    $orderValueSurcharges: ApplySurchargeInput
    $addressCorrectionSurcharges: ApplySurchargeInput
  ) {
    Courier_setSurcharges(
      courier: $courier
      rateCalculations: $rateCalculations
      cashOnDeliveries: $cashOnDeliveries
      insurances: $insurances
      remoteZones: $remoteZones
      weightSurcharges: $weightSurcharges
      dimensionSurcharges: $dimensionSurcharges
      exportDuties: $exportDuties
      fuelSurcharges: $fuelSurcharges
      parcelSurcharges: $parcelSurcharges
      orderValueSurcharges: $orderValueSurcharges
      addressCorrectionSurcharges: $addressCorrectionSurcharges
    ) {
      rateCalculations {
        applyOnAllShippingMethods
        surcharges
      }
      cashOnDeliveries {
        applyOnAllShippingMethods
        surcharges
      }
      insurances {
        applyOnAllShippingMethods
        surcharges
      }
      fuelSurcharges {
        applyOnAllShippingMethods
        surcharges
      }
      exportDuties {
        applyOnAllShippingMethods
        surcharges
      }
      dimensionSurcharges {
        applyOnAllShippingMethods
        surcharges
      }
      remoteZones {
        applyOnAllShippingMethods
        surcharges
      }
      weightSurcharges {
        applyOnAllShippingMethods
        surcharges
      }
      parcelSurcharges {
        applyOnAllShippingMethods
        surcharges
      }
      orderValueSurcharges {
        applyOnAllShippingMethods
        surcharges
      }
      addressCorrectionSurcharges {
        applyOnAllShippingMethods
        surcharges
      }
    }
  }
`;

export const UpdateCourierCurrencyMutation = gql`
  mutation Courier_setCurrency($courierId: ID!, $currency: String!) {
    Courier_setCurrency(courierId: $courierId, currency: $currency)
  }
`;

export const DuplicateCourierContractMutation = gql`
  mutation Courier_duplicate($courierId: ID!) {
    Courier_duplicate(courierId: $courierId)
  }
`;

export const UpdateUserAcceptedPrintTermsMutation = gql`
  mutation User_acceptPrintTerms($input: Boolean!) {
    User_acceptPrintTerms(input: $input) {
      id
      email
      firstname
      lastname
      language
      roles
      timeZone
      position
      viewAllDeskFolder
      deskNotificationSound
      acceptedPrintTerms
      signature
      signatures {
        language
        signature
      }
      emailOnlySignature
      status
      allowedWarehouses
    }
  }
`;

export const AddNewOrderViewMutation = gql`
  mutation OrderView_addView($input: OrderViewInput!) {
    OrderView_addView(input: $input) {
      __typename
      id
      store
      name
      icon
      couriers
      brandings
      destinations
      statuses
      warehouses
      tags
      ordering
      incidents
      threePls
    }
  }
`;

export const AddNewOrderViewForOrdersMutation = gql`
  mutation OrderView_addViewForOrders($input: OrderViewInput!) {
    OrderView_addViewForOrders(input: $input) {
      id
      store
      name
      icon
      place
      couriers
      brandings
      destinations
      statuses
      incidents
      warehouses
      tags
      threePls
      sources
      cmsStatuses
      cmsShippingMethods
      sku
      productName
      ordering
    }
  }
`;

export const UpdateOrderViewMutation = gql`
  mutation OrderView_updateDetails($viewId: ID!, $input: OrderViewInput!) {
    OrderView_updateDetails(viewId: $viewId, input: $input) {
      __typename
      id
      store
      name
      icon
      couriers
      brandings
      destinations
      statuses
      warehouses
      tags
      ordering
      incidents
      threePls
    }
  }
`;

export const UpdateViewForOrderMutation = gql`
  mutation OrderView_updateDetailsForOrders($viewId: ID!, $input: OrderViewInput!) {
    OrderView_updateDetailsForOrders(viewId: $viewId, input: $input) {
      id
      store
      name
      icon
      place
      couriers
      brandings
      destinations
      statuses
      incidents
      warehouses
      tags
      threePls
      sources
      cmsStatuses
      cmsShippingMethods
      sku
      productName
      ordering
    }
  }
`;

export const UpdateOrderViewsOrderingMutation = gql`
  mutation OrderView_updateOrdering($sortedIds: [ID]!) {
    OrderView_updateOrdering(sortedIds: $sortedIds)
  }
`;

export const UpdateViewsOrderingForOrdersMutation = gql`
  mutation OrderView_updateOrderingForOrders($sortedIds: [ID]!) {
    OrderView_updateOrderingForOrders(sortedIds: $sortedIds)
  }
`;

export const DeleteOrderViewMutation = gql`
  mutation OrderView_remove($viewId: ID!) {
    OrderView_remove(viewId: $viewId)
  }
`;

export const DeleteOrderViewForOrdersMutation = gql`
  mutation OrderView_removeForOrders($viewId: ID!) {
    OrderView_removeForOrders(viewId: $viewId)
  }
`;

export const AddInternalNoteMutation = gql`
  mutation Order_addComment($input: OrderCommentInput!) {
    Order_addComment(input: $input) {
      _id
      order
      store
      message {
        type
        children
      }
      createdBy {
        _id
        firstname
        lastname
        status
      }
      mentions {
        _id
        firstname
        lastname
        status
      }
      createdAt
      updatedAt
    }
  }
`;

export const OrdersTableConfigUpsertMutation = gql`
  mutation OrdersTableConfig_upsert($input: OrdersTableConfigInput!) {
    OrdersTableConfig_upsert(input: $input) {
      id
      store
      user
      type
      columnOrder
      columnVisibility
      columnSizing
      createdAt
      updatedAt
    }
  }
`;
